import React, { Component, Fragment } from 'react';
import AvatarEditor from 'react-avatar-editor';

import { AsyncImage } from 'components/asyncImage';
import { Loader } from 'components/loader';
import { I18n } from 'components/translate';
import { ENTITY_TYPE, UPLOAD_STATUS } from 'modules/file/constants';
import { getFileLink } from 'modules/file/helpers';
import PropTypes from 'prop-types';

import { UploadConsumer, UploadProptypes } from '@hotmart/club-files-ui';

import './ImageUploader.scss';

@UploadConsumer(ENTITY_TYPE.QUIZ_PROFILE_IMG)
export default class ImageUploader extends Component {
  imageForm = React.createRef();
  imageController = React.createRef();
  editor = React.createRef();

  static propTypes = {
    badge: PropTypes.object,
    deleteFile: PropTypes.func,
    images: PropTypes.array,
    maxFileSize: PropTypes.number,
    onChange: PropTypes.func,
    fileUpload: UploadProptypes.fileUpload,
    profileId: PropTypes.string,
    onRemoveImage: PropTypes.func
  };

  state = {
    file: '',
    imageBase64: '',
    imgSaved: false
  };

  get fileUpload() {
    return this.props.fileUpload;
  }

  setFile(file) {
    this.setState({ file });
  }

  setImage(imageBase64) {
    this.setState({ imageBase64 });
  }

  removeCurrentImage() {
    const { images = [], onRemoveImage } = this.props;

    const image = images[0] || {};
    const { id } = image;

    this.setFile();
    this.setImage();

    onRemoveImage && onRemoveImage(image);
    this.fileUpload && this.fileUpload.actions.delete(id);
  }

  _handleRemoveImage = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.imageForm.current.reset();

    this.removeCurrentImage();
  };

  _handleChangeImage = (e) => {
    e.preventDefault();
    e.stopPropagation();

    this.imageController.current.click();
    this.removeCurrentImage();
  };

  _handleInputFileChange = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.target.files[0];

    file && this.setFile(file);
  };

  static isFileUploaded({ origin, status } = {}) {
    return (
      !origin || (origin === 'UPLOAD' && status === UPLOAD_STATUS.FINISHED)
    );
  }

  static isFileUploading(image = {}) {
    const { origin, status } = image;

    return origin === 'UPLOAD' && status === UPLOAD_STATUS.STARTED;
  }

  static getDerivedBase64(image = {}, { imageBase64, imgSaved }) {
    const newId = image.id || image.imageId;
    const isFileUploaded = newId && ImageUploader.isFileUploaded(image);

    if (newId && isFileUploaded) {
      return getFileLink(newId);
    } else if (imgSaved) {
      return imageBase64;
    }
  }

  static getDerivedStateFromProps({ images = [] }, state) {
    return {
      imageBase64: ImageUploader.getDerivedBase64(images[0], state),
      isUploading: ImageUploader.isFileUploading(images[0]),
      imgSaved: images[0] && ImageUploader.isFileUploaded(images[0])
    };
  }

  get imagePreview() {
    const { imageBase64 } = this.state;

    this.classNamePreview = 'imgPreview';

    if (imageBase64) {
      this.classNamePreview += ' hasPreview';
      return (
        <Fragment>
          <AsyncImage url={imageBase64} />
          <div className="img-preview-actions">
            <button
              className="btn btn-change"
              onClick={this._handleChangeImage}
            >
              <I18n label="general.change" />
            </button>
            <button
              className="btn btn-remove"
              onClick={this._handleRemoveImage}
            >
              <I18n label="general.remove" />
            </button>
          </div>
        </Fragment>
      );
    } else {
      return (
        <div>
          <i className="icon icon-image" />
          <p>
            <I18n label="files.image.send_image" />
          </p>
        </div>
      );
    }
  }

  render() {
    const $imagePreview = this.imagePreview;

    return (
      <Fragment>
        <div className="previewComponent">
          <form ref={this.imageForm} onSubmit={this._handleSubmit}>
            <label ref={this.imageController}>
              <input
                className="fileInput hidden"
                type="file"
                accept="image/*"
                onChange={this._handleInputFileChange}
                name="imageSubmitter"
              />

              <div className={this.classNamePreview}>
                {!this.state.file && !this.state.imgSaved && $imagePreview}
                {this.state.imgSaved && $imagePreview}
              </div>
            </label>

            {this.renderCrop()}
          </form>
        </div>
      </Fragment>
    );
  }

  renderCrop() {
    const { isUploading, file, imgSaved } = this.state;

    return (
      <div className="crop-badge">
        {isUploading && <Loader />}
        {file && !imgSaved && (
          <AvatarEditor
            ref={this.editor}
            image={file}
            width={147}
            height={147}
            border={0}
            borderRadius={100}
            // eslint-disable-next-line @typescript-eslint/no-magic-numbers
            color={[35, 35, 35, 0.6]}
          />
        )}
        <div className="img-preview-actions">
          {file && !imgSaved && (
            <button className="btn btn-save" onClick={this.onClickSave}>
              <I18n label="general.save" />
            </button>
          )}
        </div>
      </div>
    );
  }

  onClickSave = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (this.editor.current) {
      const dataURL = this.editor.current.getImageScaledToCanvas().toDataURL();

      fetch(dataURL)
        .then((res) => res.blob())
        .then((blob) => this.saveFile(blob, dataURL));
    }
  };

  saveFile = (blob, dataURL) => {
    const { profileId } = this.props;
    const timestamp = Date.now();
    const file = new File([blob], this.state.file.name, {
      type: blob.type,
      lastModified: timestamp
    });

    this.setImage(dataURL);
    this.fileUpload && this.fileUpload.actions.send(file, { profileId });
  };
}
